import { useCallback, useEffect, useRef, useState } from "react";

const G = 9.81;

// Surveillance PTI : capteurs de mouvement, verticalité, minuterie « tout va bien »
export function usePtiMonitor(cfg, active, paused, onTrigger) {
  const trig = useRef(onTrigger);
  trig.current = onTrigger;
  const st = useRef({});
  const [checkinDue, setCheckinDue] = useState(null);
  const [tilted, setTilted] = useState(false);

  const resetCheckin = useCallback(() => {
    setCheckinDue(cfg?.checkin_minutes ? Date.now() + cfg.checkin_minutes * 60000 : null);
  }, [cfg]);

  useEffect(() => {
    if (!active || !cfg) return undefined;
    const s = st.current;
    s.lastMove = Date.now(); s.tiltSince = null; s.spikeAt = null;
    const onMotion = (e) => {
      const a = e.accelerationIncludingGravity;
      if (!a || a.x == null) return;
      const mag = Math.sqrt(a.x * a.x + a.y * a.y + a.z * a.z) / G;
      const now = Date.now();
      if (Math.abs(mag - 1) > 0.12) s.lastMove = now;
      if (cfg.fall_detection && mag > 2.5) s.spikeAt = now;
      const vertical = Math.abs(a.y) / G > 0.5;
      if (!vertical && s.tiltSince == null) s.tiltSince = now;
      if (vertical) s.tiltSince = null;
      setTilted(!vertical);
    };
    window.addEventListener("devicemotion", onMotion);
    const iv = setInterval(() => {
      if (s.paused) return;
      const now = Date.now();
      if (s.spikeAt && now - s.spikeAt > 3000) {
        const still = now - s.lastMove > 2000;
        s.spikeAt = null;
        if (still || s.tiltSince) { trig.current("fall"); return; }
      }
      if (s.tiltSince && now - s.tiltSince > cfg.tilt_seconds * 1000) { s.tiltSince = null; trig.current("fall"); return; }
      if (now - s.lastMove > cfg.no_motion_seconds * 1000) { s.lastMove = now; trig.current("no_motion"); return; }
      if (s.checkinDue && now > s.checkinDue) { s.checkinDue = null; trig.current("checkin"); }
    }, 1000);
    return () => { window.removeEventListener("devicemotion", onMotion); clearInterval(iv); };
  }, [active, cfg]);

  useEffect(() => { st.current.paused = paused; if (!paused) { st.current.lastMove = Date.now(); st.current.tiltSince = null; } }, [paused]);
  useEffect(() => { st.current.checkinDue = checkinDue; }, [checkinDue]);
  useEffect(() => { if (active) resetCheckin(); else setCheckinDue(null); }, [active, resetCheckin]);

  return { checkinDue, resetCheckin, tilted };
}

// Garde l'écran allumé tant que le PTI est actif
export function useWakeLock(active) {
  useEffect(() => {
    if (!active || !("wakeLock" in navigator)) return undefined;
    let lock = null;
    const get = () => navigator.wakeLock.request("screen").then((l) => { lock = l; }).catch(() => {});
    const vis = () => document.visibilityState === "visible" && get();
    get();
    document.addEventListener("visibilitychange", vis);
    return () => { document.removeEventListener("visibilitychange", vis); lock?.release().catch(() => {}); };
  }, [active]);
}

// Bip d'alerte répété pendant la pré-alarme
export function useAlarmSound(on) {
  useEffect(() => {
    if (!on) return undefined;
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return undefined;
    const ctx = new Ctx();
    const beep = () => {
      const o = ctx.createOscillator(), g = ctx.createGain();
      o.frequency.value = 1400; o.connect(g); g.connect(ctx.destination);
      g.gain.setValueAtTime(0.4, ctx.currentTime); o.start(); o.stop(ctx.currentTime + 0.25);
      navigator.vibrate?.([300, 150, 300]);
    };
    beep();
    const iv = setInterval(beep, 1000);
    return () => { clearInterval(iv); ctx.close().catch(() => {}); navigator.vibrate?.(0); };
  }, [on]);
}
